import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import Select from "react-select";
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from "../../../pages/staff/dropdownStyles";

const CountryCitySelector = ({
  selectedCountry,
  selectedCity,
  setSelectedCity,
  onChange,
  setLoading,
  showCountryField = true,
  showCityField = true,
  cityLabel = "City",
  cityPlaceholder = "Select City",
  className,
  countryClassName,
  cityClassName,
  inlineLayout = false,
  selectedValueIsLabel = false,
  countryFieldName = "country",
  cityFieldName = "city",
  isDarkMode = false,
}) => {
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);

  const dropdownStyles = useMemo(() => {
    const base = isDarkMode ? DropDownStylesDark : DropDownStyles;
    return {
      ...base,
      container: (provided) => ({
        ...provided,
        flex: 1,
        minWidth: 0,
        width: "100%",
      }),
      control: (provided, state) => ({
        ...(base.control ? base.control(provided, state) : provided),
        minHeight: "38px",
        width: "100%",
      }),
    };
  }, [isDarkMode]);

  const fetchCountries = useCallback(async () => {
    try {
      setLoading && setLoading(true);
      const response = await axiosInstance.get(APIs.GET_COUNTRIES);
      const raw = response.data?.data ?? response.data ?? [];
      const countryData = Array.isArray(raw) ? raw : [];

      const mapped = countryData
        .map((country) => {
          const code = String(country.code ?? country.Code ?? "").trim();
          const code2 = String(country.code2 ?? country.Code2 ?? "").trim();
          const name = String(country.name ?? country.Name ?? "").trim();
          if (!name && !code && !code2) return null;
          return {
            // Prefer ISO3 Code; fall back to Code2 when Code is missing
            value: code || code2,
            label: name || code || code2,
            code,
            code2,
          };
        })
        .filter(Boolean);

      setCountries(mapped);
    } catch (err) {
      setCountries([]);
    } finally {
      setLoading && setLoading(false);
    }
  }, [setLoading]);

  const fetchCities = useCallback(
    async (countryCode) => {
      if (!countryCode) {
        setCities([]);
        return 0;
      }

      try {
        setLoading && setLoading(true);
        const response = await axiosInstance.get(APIs.GET_ALL_CITIES, {
          params: { CountryCode: countryCode },
        });
        const raw = response.data?.data ?? response.data ?? [];
        const cityData = Array.isArray(raw) ? raw : [];

        const mappedCities = cityData.map((city) => {
          const name = city.cit_Name ?? city.Cit_Name ?? city.name ?? city.Name ?? "";
          const id = city.cit_ID ?? city.Cit_ID ?? city.id ?? city.ID ?? name;
          return {
            value: selectedValueIsLabel ? name : id,
            label: name,
          };
        });
        setCities(mappedCities);
        return mappedCities.length;
      } catch (err) {
        setCities([]);
        return 0;
      } finally {
        setLoading && setLoading(false);
      }
    },
    [setLoading, selectedValueIsLabel]
  );

  const resolveCountryOption = useCallback(
    (selected) => {
      if (!selected) return null;
      const key = String(selected).trim();
      if (!key) return null;
      const upper = key.toUpperCase();

      if (!countries.length) {
        // Keep a visible selected value while options are still loading
        return { value: key, label: key };
      }

      return (
        countries.find(
          (c) =>
            String(c.value || "").toUpperCase() === upper ||
            String(c.code || "").toUpperCase() === upper ||
            String(c.code2 || "").toUpperCase() === upper ||
            String(c.label || "").toLowerCase() === key.toLowerCase()
        ) || { value: key, label: key }
      );
    },
    [countries]
  );

  const handleCountryChange = (option) => {
    const countryCode = option?.value || "";

    if (onChange) {
      onChange({
        [countryFieldName]: countryCode,
        [cityFieldName]: "",
      });
    }

    if (setSelectedCity) {
      setSelectedCity("");
    }

    if (countryCode) {
      fetchCities(countryCode);
    } else {
      setCities([]);
    }
  };

  const handleCityChange = (option, actionMeta) => {
    let cityValue = "";
    if (option) {
      if (actionMeta && actionMeta.action === "create-option") {
        cityValue = option.label;
      } else {
        cityValue = selectedValueIsLabel ? option.label : option.value;
      }
    }

    if (setSelectedCity) {
      setSelectedCity(cityValue);
    }

    if (onChange) {
      onChange({
        [countryFieldName]: selectedCountry,
        [cityFieldName]: cityValue,
      });
    }
  };

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    if (!selectedCountry) {
      setCities([]);
      return;
    }

    let cancelled = false;

    const loadCitiesForCountry = async () => {
      const matched = resolveCountryOption(selectedCountry);
      const primary =
        matched?.code || matched?.value || String(selectedCountry).trim();
      const secondary = matched?.code2 || "";

      const count = await fetchCities(primary);
      if (cancelled) return;

      if (
        count === 0 &&
        secondary &&
        secondary.toUpperCase() !== String(primary).toUpperCase()
      ) {
        await fetchCities(secondary);
      }
    };

    loadCitiesForCountry();
    return () => {
      cancelled = true;
    };
  }, [selectedCountry, fetchCities, resolveCountryOption]);

  const countryValue = resolveCountryOption(selectedCountry);

  const cityValue = selectedCity
    ? cities.find((c) =>
        selectedValueIsLabel
          ? c.label === selectedCity
          : String(c.value) === String(selectedCity)
      ) || { value: selectedCity, label: selectedCity }
    : null;

  const containerClass = inlineLayout
    ? `d-flex flex-row gap-3 w-100 mb-0`
    : className || "row g-3";

  const countryContainerClass = inlineLayout
    ? `${countryClassName || "flex-grow-1"}`
    : "col-md-6";

  const cityContainerClass = inlineLayout
    ? `${cityClassName || "flex-grow-1"}`
    : showCountryField
      ? "col-md-6"
      : "col-md-12";

  const showIndividualLabels = !inlineLayout;

  return (
    <div className={containerClass}>
      {showCountryField && (
        <div className={countryContainerClass}>
          {showIndividualLabels && (
            <label
              className={`form-label ${isDarkMode ? "text-white" : "text-gray-700"}`}
            >
              Country
            </label>
          )}
          <div className="d-flex align-items-stretch w-100 country-city-selector__country">
            <span
              className={`d-flex align-items-center justify-content-center px-3 ${
                isDarkMode ? "bg-dark text-white" : "bg-light text-muted"
              }`}
              style={{
                border: isDarkMode ? "1px solid #4b5563" : "1px solid #adb5bd",
                borderRight: "none",
                borderRadius: "6px 0 0 6px",
                flexShrink: 0,
              }}
            >
              <i className="fa fa-globe" aria-hidden="true" />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Select
                value={countryValue}
                onChange={handleCountryChange}
                options={countries}
                styles={{
                  ...dropdownStyles,
                  control: (provided, state) => ({
                    ...(dropdownStyles.control
                      ? dropdownStyles.control(provided, state)
                      : provided),
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    minHeight: "38px",
                    width: "100%",
                  }),
                }}
                placeholder="Select Country"
                isClearable={true}
                isSearchable={true}
                {...defaultSelectPortalProps}
              />
            </div>
          </div>
        </div>
      )}
      {showCityField && (
        <div className={cityContainerClass}>
          {showIndividualLabels && (
            <label
              className={`form-label ${isDarkMode ? "text-white" : "text-gray-700"}`}
            >
              {cityLabel}
            </label>
          )}
          <Select
            value={cityValue}
            onChange={handleCityChange}
            options={cities}
            styles={dropdownStyles}
            placeholder={cityPlaceholder || "Select City"}
            isClearable={true}
            isDisabled={!selectedCountry}
            isSearchable={true}
            {...defaultSelectPortalProps}
          />
        </div>
      )}
    </div>
  );
};

export default CountryCitySelector;
